import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} MemberType
 * @property {string} firstName - First Name of member
 * @property {string} lastName - Last Name of member
 * @property {string} email - Email of member
 * @property {string} role_slugs - Role slug of member
 */

/**
 * @description Custom hook to create new members in an organization
 */
export const useCreateMember = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [member, setMember] = useState(null);
  const { axiosInstance } = useAxios();

  /**
   * @description Create a new member
   * @async
   * @param {MemberType} memberData - The member data to create
   * @param {()=> void} [onSuccess] - Callback function to be called on success
   * @returns {Promise<void>} Resolves once the API request completes
   */
  const createMember = async (memberData, onSuccess) => {
    try {
      setLoading(true);
      setError("");
      setSuccess(false);

      const response = await axiosInstance.post(
        `${TENANT_DOMAIN}/school/admin/member/store`,
        {
          firstName: memberData.firstName,
          lastName: memberData.lastName,
          email: memberData.email,
          role_slug: memberData.role_slug,
        },
      );

      if (onSuccess) onSuccess();
      setSuccess(true);
      setMember(response.data?.data || response.data);
    } catch (err) {
      setSuccess(false);
      setMember(null);
      if (err?.response?.status === 500 || err?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        const apiErrors = err?.response?.data?.errors;
        const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

        setError(
          firstError || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, success, member, createMember };
};
